import React, { useState, useEffect, useRef } from "react";
import { ResizableBox } from "react-resizable";
import { BsBookmark, BsBookmarkFill } from "react-icons/bs";
import { ImSpellCheck } from "react-icons/im";
import { motion } from "framer-motion";
import 'katex/dist/katex.min.css';
import { IoExpand } from "react-icons/io5";

const Question = ({
    sat,
    handleAnswerValueChange,
    handleCrossOutOptions,
    handleSelectOption,
    handleBookMark,
    currentQuestion,
}) => {
    // refs & state (값, 함수는 그대로 유지)
    const boxItemLeftRef = useRef();
    const containerRef = useRef(null);
    const dividerRef = useRef(null);
    const [showCrossOutMenu, setShowCrossOutMenu] = useState(false);
    const [crossedOutOptions, setCrossedOutOptions] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isKatexRendered, setIsKatexRendered] = useState(false);

    // 모바일 여부 판단 (화면 폭 768px 미만이면 모바일)
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // PC 전용 상태 (좌우 레이아웃에서 왼쪽 Pane의 너비 조절)
    const [leftWidthPx, setLeftWidthPx] = useState(0);
    const [rightWidthPx, setRightWidthPx] = useState(0);

    useEffect(() => {
        if (containerRef.current && !isMobile) {
            const containerWidth = containerRef.current.getBoundingClientRect().width;
            const half = containerWidth / 2;
            setLeftWidthPx(half);
            setRightWidthPx(half);
        }
    }, [isMobile]);

    // 리사이징 이벤트 핸들러 (값 그대로)
    const [isResizing, setIsResizing] = useState(false);
    const onMouseDown = (e) => {
        setIsResizing(true);
    };

    const onMouseMove = (e) => {
        if (!isResizing || !containerRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerLeft = containerRect.left;
        const containerWidth = containerRect.width;

        // 현재 마우스 X 좌표에서 컨테이너 왼쪽 좌표를 빼서 새 leftWidth 계산
        let newLeftWidth = e.clientX - containerLeft;

        // 최소, 최대 너비를 컨테이너의 30% ~ 70% (px 기준)로 제한
        const minWidth = containerWidth * 0.3;
        const maxWidth = containerWidth * 0.7;
        if (newLeftWidth < minWidth) newLeftWidth = minWidth;
        if (newLeftWidth > maxWidth) newLeftWidth = maxWidth;
        setLeftWidthPx(newLeftWidth);
    };

    const onMouseUp = () => {
        setIsResizing(false);
    };

    useEffect(() => {
        if (isResizing) {
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
            document.body.style.userSelect = "none";
        } else {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.userSelect = "auto";
        }
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.userSelect = "auto";
        };
    }, [isResizing]);

    // KaTeX 스크립트 로드 (값 그대로)
    useEffect(() => {
        const script2 = document.createElement("script");
        script2.src =
            "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js";
        script2.integrity =
            "sha384-+VBxd3r6XgURycqtZ117nYw44OOcIax56Z4dCRWbxyPt0Koah1uHoK0o4+/RRE05";
        script2.crossOrigin = "anonymous";

        script2.onload = () => {
            if (containerRef.current) {
                const options = {
                    delimiters: [
                        { left: "$$", right: "$$", display: false },
                        { left: "$", right: "$", display: false },
                        { left: "\\(", right: "\\)", display: false },
                        { left: "\\[", right: "\\]", display: true },
                        { left: "\\begin{array}", right: "\\end{array}", display: true },
                    ],
                    throwOnError: false,
                };
                window.renderMathInElement(containerRef.current, options);
                setIsKatexRendered(true);
            }
        };
        document.body.appendChild(script2);
        return () => {
            document.body.removeChild(script2);
        };
    }, [currentQuestion]);

    // 확장 토글 (필요시)
    const handleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    // 모바일과 PC의 렌더링 분기 처리
    if (isMobile) {
        if (currentQuestion.a !== null && currentQuestion.a !== "null") {
            // MCQ 모바일 레이아웃: 도구바 → 문제 내용(영어인 경우 stimulus + stem, 수학인 경우 stem) → 옵션 (세로 나열)
            return (
                <div id="box-item-left" ref={containerRef} className="mt-[20px]">
                    {/* 상단 도구바 */}
                    <div className="flex flex-row justify-between bg-gray-100 p-2">
                        <div className="flex flex-row gap-2 items-center">
                            <p className="py-2 px-3 bg-black text-white">
                                {currentQuestion.index + 1}
                            </p>
                            <div className="flex flex-row items-center gap-1">
                                <motion.div
                                    whileTap={{ scale: 0.97 }}
                                    onClick={() => handleBookMark(currentQuestion.questionIds)}
                                    className="m-1 cursor-pointer"
                                >
                                    {currentQuestion.bookMarked ? (
                                        <BsBookmarkFill color="red" size="1.2rem" />
                                    ) : (
                                        <BsBookmark size="1.2rem" />
                                    )}
                                </motion.div>
                                <div>
                                    <p>Mark For Review</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row items-center">
                            <motion.div
                                whileTap={{ scale: 0.98 }}
                                className={`p-[5px] border-black border-[1px] rounded-md cursor-pointer ${showCrossOutMenu && "bg-blue-700 text-white"}`}
                                onClick={() => setShowCrossOutMenu((pre) => !pre)}
                            >
                                <ImSpellCheck />
                            </motion.div>
                        </div>
                    </div>
                    {/* 문제 내용 */}
                    <div className="mt-4 p-4">
                        {sat === "Math" || sat === "math" ? (
                            <div
                                id="test-imgsat"
                                dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                                className="stem-content"
                            />
                        ) : (
                            <div
                                dangerouslySetInnerHTML={{ __html: currentQuestion.stimulus }}
                                className="stem-content"
                            />
                        )}
                    </div>
                    {/* 영어 문제인 경우 추가 stem 내용 */}
                    {sat !== "Math" && sat !== "math" && (
                        <div className="mt-4 p-4">
                            <div
                                dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                                className="stem-content"
                            />
                        </div>
                    )}
                    {/* 옵션 영역 (세로로 나열) */}
                    <div className="mt-4 p-4 mb-[100px]">
                        <Option
                            handleCrossOutOptions={handleCrossOutOptions}
                            currentQuestion={currentQuestion}
                            handleSelectOption={handleSelectOption}
                            idx={0}
                            showCrossOutMenu={showCrossOutMenu}
                            crossedOutOptions={crossedOutOptions}
                            data={currentQuestion.a}
                        />
                        <Option
                            handleCrossOutOptions={handleCrossOutOptions}
                            currentQuestion={currentQuestion}
                            handleSelectOption={handleSelectOption}
                            idx={1}
                            showCrossOutMenu={showCrossOutMenu}
                            crossedOutOptions={crossedOutOptions}
                            data={currentQuestion.b}
                        />
                        <Option
                            handleCrossOutOptions={handleCrossOutOptions}
                            currentQuestion={currentQuestion}
                            handleSelectOption={handleSelectOption}
                            idx={2}
                            showCrossOutMenu={showCrossOutMenu}
                            crossedOutOptions={crossedOutOptions}
                            data={currentQuestion.c}
                        />
                        <Option
                            handleCrossOutOptions={handleCrossOutOptions}
                            currentQuestion={currentQuestion}
                            handleSelectOption={handleSelectOption}
                            idx={3}
                            showCrossOutMenu={showCrossOutMenu}
                            crossedOutOptions={crossedOutOptions}
                            data={currentQuestion.d}
                        />
                    </div>
                </div>
            );
        } else {
            // FRQ 모바일 레이아웃: 도구바 → 문제 내용 → 입력창 (양쪽 여백을 줄여 전체폭 활용)
            return (
                <div id="box-item-left" ref={containerRef} className="mt-[20px] h-full px-4">
                    {/* 상단 도구바 */}
                    <div className="flex flex-row justify-between bg-gray-100 p-2">
                        <div className="flex flex-row gap-2 items-center">
                            <p className="py-2 px-3 bg-black text-white">
                                {currentQuestion.index + 1}
                            </p>
                            <div className="flex flex-row items-center gap-1">
                                <motion.div
                                    whileTap={{ scale: 0.97 }}
                                    onClick={() => handleBookMark(currentQuestion.questionIds)}
                                    className="m-1 cursor-pointer"
                                >
                                    {currentQuestion.bookMarked ? (
                                        <BsBookmarkFill color="red" size="1.2rem" />
                                    ) : (
                                        <BsBookmark size="1.2rem" />
                                    )}
                                </motion.div>
                                <div>
                                    <p>Mark For Review</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row items-center">
                            <motion.div
                                whileTap={{ scale: 0.98 }}
                                className={`p-[5px] border-black border-[1px] rounded-md cursor-pointer ${showCrossOutMenu && "bg-blue-700 text-white"}`}
                                onClick={() => setShowCrossOutMenu((pre) => !pre)}
                            >
                                <ImSpellCheck />
                            </motion.div>
                        </div>
                    </div>
                    {/* 문제 내용 */}
                    <div className="mt-4 p-4">
                        <div
                            id="test-imgsat"
                            dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                            className="stem-content"
                        />
                    </div>
                    {/* 답안 입력 */}
                    <div className="mt-4 p-4">
                        <input
                            className="w-[100%] border-[1px] py-1 px-2 border-gray-300 outline-none rounded-md"
                            type="text"
                            value={currentQuestion.selectedOption || ""}
                            onChange={(e) => {
                                const re = /^[-0-9./]*$/;
                                if (re.test(e.target.value)) {
                                    handleAnswerValueChange(currentQuestion.questionIds, e.target.value);
                                }
                            }}
                        />
                    </div>
                </div>
            );
        }
    } else {
        // PC 버전: 기존의 두 Pane(좌우 레이아웃) 디자인 그대로 유지
        return (
            <div id="box-item-left" ref={containerRef}>
                {currentQuestion.a !== null && currentQuestion.a !== "null" ? (
                    // 객관식(좌우 레이아웃) 디자인 적용
                    <div className="mt-[20px] flex h-full">
                        {/* 왼쪽 Pane */}
                        <div
                            className="left-pane"
                            ref={boxItemLeftRef}
                            style={{
                                width: `${leftWidthPx}px`,
                                overflow: "auto",
                                backgroundColor: "white",
                            }}
                        >
                            <div className="mx-[34px] mb-[160px] mt-[3%] z-[10]">
                                <div className="flex flex-col justify-start ml-2 mr-2 box-item-left">
                                    <div>
                                        {sat === "Math" ? (
                                            <div
                                                id="test-imgsat"
                                                dangerouslySetInnerHTML={{
                                                    __html: currentQuestion.stem,
                                                }}
                                                className="m-4 min-h-[40%] overflow-y-auto my-list-style stem-content scrollbar-hide max-w-full"
                                            />
                                        ) : (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: currentQuestion.stimulus,
                                                }}
                                                className="min-h-[40%] overflow-y-auto overflow-x-hidden my-list-style stem-content"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="relative h-screen">
                            <div
                                ref={dividerRef}
                                className="divider mt-[20px] h-1/2 bg-gray-300 cursor-col-resize"
                                onMouseDown={onMouseDown}
                            />
                        </div>

                        {/* 오른쪽 Pane */}
                        <div
                            className="right-pane ml-[15px] mr-[15px] m-4"
                            style={{ flex: 1, overflow: "auto" }}
                        >
                            {/* 상단 도구바 */}
                            <div className="flex flex-row justify-between bg-gray-100">
                                <div className="flex flex-row gap-2">
                                    <div className="flex flex-row items-center">
                                        <p className="py-2 px-3 bg-black text-white">
                                            {currentQuestion.index + 1}
                                        </p>
                                    </div>
                                    <div className="flex flex-row items-center gap-1">
                                        <motion.div
                                            whileTap={{ scale: 0.97 }}
                                            onClick={() => handleBookMark(currentQuestion.questionIds)}
                                            className="m-1 cursor-pointer"
                                        >
                                            {currentQuestion.bookMarked ? (
                                                <BsBookmarkFill color="red" size="1.2rem" />
                                            ) : (
                                                <BsBookmark size="1.2rem" />
                                            )}
                                        </motion.div>
                                        <div>
                                            <p>Mark For Review</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row items-center">
                                    <motion.div
                                        whileTap={{ scale: 0.98 }}
                                        className={`p-[5px] border-black border-[1px] rounded-md cursor-pointer ${showCrossOutMenu && "bg-blue-700 text-white"
                                            }`}
                                        onClick={() => setShowCrossOutMenu((pre) => !pre)}
                                    >
                                        <ImSpellCheck />
                                    </motion.div>
                                </div>
                            </div>

                            {sat !== "Math" && sat !== "math" && (
                                <div>
                                    <div
                                        className="box-item-left"
                                        dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                                    />
                                </div>
                            )}

                            {/* 옵션 영역 */}
                            <div className="my-2 min-w-[100px] overflow-y-auto">
                                <div className="w-full p-4 rounded-xl z-[99999]">
                                    <Option
                                        handleCrossOutOptions={handleCrossOutOptions}
                                        currentQuestion={currentQuestion}
                                        handleSelectOption={handleSelectOption}
                                        idx={0}
                                        showCrossOutMenu={showCrossOutMenu}
                                        crossedOutOptions={crossedOutOptions}
                                        data={currentQuestion.a}
                                    />
                                    <Option
                                        handleCrossOutOptions={handleCrossOutOptions}
                                        currentQuestion={currentQuestion}
                                        handleSelectOption={handleSelectOption}
                                        idx={1}
                                        showCrossOutMenu={showCrossOutMenu}
                                        crossedOutOptions={crossedOutOptions}
                                        data={currentQuestion.b}
                                    />
                                    <Option
                                        handleCrossOutOptions={handleCrossOutOptions}
                                        currentQuestion={currentQuestion}
                                        handleSelectOption={handleSelectOption}
                                        idx={2}
                                        showCrossOutMenu={showCrossOutMenu}
                                        crossedOutOptions={crossedOutOptions}
                                        data={currentQuestion.c}
                                    />
                                    <Option
                                        handleCrossOutOptions={handleCrossOutOptions}
                                        currentQuestion={currentQuestion}
                                        handleSelectOption={handleSelectOption}
                                        idx={3}
                                        showCrossOutMenu={showCrossOutMenu}
                                        crossedOutOptions={crossedOutOptions}
                                        data={currentQuestion.d}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    // 주관식 또는 currentQuestion.a가 null인 경우 (디자인 적용)
                    <div className="mt-[20px] h-full ml-[20%] mr-[20%]">
                        <div className="flex flex-row justify-between bg-gray-100">
                            <div className="flex flex-row gap-2">
                                <div className="flex flex-row items-center">
                                    <p className="py-2 px-3 bg-black text-white">
                                        {currentQuestion.index + 1}
                                    </p>
                                </div>
                                <div className="flex flex-row items-center gap-1">
                                    <motion.div
                                        whileTap={{ scale: 0.97 }}
                                        onClick={() => handleBookMark(currentQuestion.questionIds)}
                                        className="m-1 cursor-pointer"
                                    >
                                        {currentQuestion.bookMarked ? (
                                            <BsBookmarkFill color="red" size="1.2rem" />
                                        ) : (
                                            <BsBookmark size="1.2rem" />
                                        )}
                                    </motion.div>
                                    <div>
                                        <p>Mark For Review</p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row items-center">
                                <motion.div
                                    whileTap={{ scale: 0.98 }}
                                    className={`p-[5px] border-black border-[1px] rounded-md cursor-pointer ${showCrossOutMenu && "bg-blue-700 text-white"
                                        }`}
                                    onClick={() => setShowCrossOutMenu((pre) => !pre)}
                                >
                                    <ImSpellCheck />
                                </motion.div>
                            </div>
                        </div>

                        <div
                            id="test-imgsat"
                            className="mt-[20px] stem-content"
                            dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                        />

                        <input
                            className="mt-[20px] w-[100%] border-[1px] py-1 px-2 border-gray-300 outline-none rounded-md"
                            type="text"
                            value={currentQuestion.selectedOption || ""}
                            onChange={(e) => {
                                const re = /^[-0-9./]*$/;
                                if (re.test(e.target.value)) {
                                    handleAnswerValueChange(
                                        currentQuestion.questionIds,
                                        e.target.value
                                    );
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }
};

export default Question;

// Option 컴포넌트 (첫 번째 컴포넌트의 디자인을 적용)
const Option = ({
    handleCrossOutOptions,
    currentQuestion,
    idx,
    handleSelectOption,
    showCrossOutMenu,
    crossedOutOptions,
    data,
}) => {
    return (
        <div className="flex flex-row gap-1">
            <motion.div
                whileTap={{ scale: 0.98 }}
                className={`flex flex-row w-full hover:bg-gray-200 ${currentQuestion.selectedOption === getOptionLetter(idx).toLowerCase()
                    ? "border-[2px] border-blue-700 bg-blue-100"
                    : "border-[1px] border-black"
                    } rounded-md gap-2 p-1 m-3 cursor-pointer relative ${currentQuestion.crossedOutOptions.includes(
                        getOptionLetter(idx).toLowerCase()
                    ) &&
                    "before:absolute before:bg-black before:w-[103%] before:h-[2px] before:top-[50%] before:-left-[6px]"
                    }`}
                onClick={() =>
                    handleSelectOption(
                        currentQuestion.questionIds,
                        getOptionLetter(idx).toLowerCase()
                    )
                }
            >
                <div className="flex flex-row items-center">
                    <p className="flex flex-row items-center justify-center p-1 w-6 h-6 border-[1px] border-black rounded-full">
                        {getOptionLetter(idx)}
                    </p>
                </div>
                <div dangerouslySetInnerHTML={{ __html: data }} />
            </motion.div>
            {showCrossOutMenu && (
                <motion.div className="flex flex-row items-center" whileTap={{ scale: 0.97 }}>
                    <div className="relative">
                        {currentQuestion.crossedOutOptions.includes(
                            getOptionLetter(idx).toLowerCase()
                        ) ? (
                            <p
                                className="cursor-pointer underline"
                                onClick={() =>
                                    handleCrossOutOptions(
                                        currentQuestion.questionIds,
                                        getOptionLetter(idx).toLowerCase()
                                    )
                                }
                            >
                                Undo
                            </p>
                        ) : (
                            <>
                                <p
                                    onClick={() =>
                                        handleCrossOutOptions(
                                            currentQuestion.questionIds,
                                            getOptionLetter(idx).toLowerCase()
                                        )
                                    }
                                    className="flex flex-row items-center justify-center p-1 w-6 h-6 border-[1px] border-black rounded-full cursor-pointer"
                                >
                                    {getOptionLetter(idx)}
                                </p>
                                <div
                                    className="absolute w-6 h-[1.5px] bg-black top-3 cursor-pointer"
                                    onClick={() =>
                                        handleCrossOutOptions(
                                            currentQuestion.questionIds,
                                            getOptionLetter(idx).toLowerCase()
                                        )
                                    }
                                ></div>
                            </>
                        )}
                    </div>
                </motion.div>
            )}
        </div>
    );
};

function getOptionLetter(idx) {
    switch (idx) {
        case 0:
            return "A";
        case 1:
            return "B";
        case 2:
            return "C";
        case 3:
            return "D";
        default:
            return "A";
    }
}
